import { useCallback, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { FlexColumn, FlexRow } from '../../atoms/layout/Flex'
import Label from '../../atoms/display/Label'
import Avatar from '../../atoms/display/Avatar'
import AvatarGroup from '../../atoms/display/AvatarGroup'
import Card from '../../atoms/surfaces/Card'
import TextBox from '../../atoms/display/TextBox'
import { ArrowBackIcon, ArrowForwardIcon, AttachmentIcon, AttachmentImageIcon } from '../../atoms/display/Icons'
import Image from '../../atoms/display/Image'
import ImageViewer from '../../atoms/display/ImageViewer'
import ImageDialog from '../../atoms/feedback/ImageDialog'
import IconButton from '../../atoms/inputs/IconButton'
import Link from '../../atoms/navigation/Link'
import { ChatMessage, ChatMessageAttachment } from '../../../types/Chat'
import useDay from '../../../hooks/useDay'
import Divider from '../../atoms/display/Divider'
import Alert from '../../atoms/feedback/Alert'
import { useDeviceType } from '../../../hooks/useDeviceType'

type MessageBoardProps = {
  currentUserId: string
  messages: ChatMessage[]
  height?: string
}

function isShowDate(index: number, messages: ChatMessage[], formatDate: (date: string) => string): boolean {
  if (index === 0) {
    return true
  }
  return formatDate(messages[index - 1].createdAt) !== formatDate(messages[index].createdAt)
}

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']

function isImageAttachment(attachment: ChatMessageAttachment): boolean {
  const lowerName = attachment.name.toLowerCase()
  return IMAGE_EXTENSIONS.some((ext) => lowerName.endsWith(ext))
}

function isShowNew(index: number, messages: ChatMessage[]) {
  if (index === 0) {
    return !messages[index].isRead
  }
  return !messages[index].isRead && messages[index - 1].isRead
}

export default function MessageBoard({ currentUserId, messages, height }: MessageBoardProps) {
  const { pc } = useDeviceType()
  const ref = useRef<HTMLDivElement>()
  const [messageCount, setMessageCount] = useState<number>()
  const [previewImages, setPreviewImages] = useState<ChatMessageAttachment[]>([])
  const [previewIndex, setPreviewIndex] = useState<number>(-1)

  const handleOpenPreview = useCallback((message: ChatMessage, attachment: ChatMessageAttachment) => {
    const images = (message.attachments ?? []).filter((a) => isImageAttachment(a))
    const index = images.findIndex((a) => a.id === attachment.id)
    setPreviewImages(images)
    setPreviewIndex(index)
  }, [])

  const handleClosePreview = useCallback(() => {
    setPreviewIndex(-1)
    setPreviewImages([])
  }, [])

  const handlePrevImage = useCallback(() => {
    setPreviewIndex((prev) => (prev > 0 ? prev - 1 : prev))
  }, [])

  const handleNextImage = useCallback(() => {
    setPreviewIndex((prev) => (prev < previewImages.length - 1 ? prev + 1 : prev))
  }, [previewImages.length])
  const { formatDate, formatTime, isAfter } = useDay()
  useEffect(() => {
    if ((ref && messageCount === undefined) || messageCount !== messages.length) {
      ref.current?.scrollIntoView()
      setMessageCount(messages.length)
    }
  }, [ref, messages, messageCount])
  const sortedMessages = messages
    .slice()
    .sort((message1, message2) => (isAfter(message1.createdAt, message2.createdAt) ? 1 : -1))
  let computedHeight = height
  if (!computedHeight) {
    if (pc) {
      computedHeight = '480px'
    } else {
      computedHeight = '360px'
    }
  }
  return (
    <FlexColumn height={computedHeight} space={2}>
      {sortedMessages.length === 0 && <Alert severity="success">メッセージはありません</Alert>}
      {sortedMessages.map((message, index) => (
        <FlexColumn key={message.id} scroll={false}>
          {isShowDate(index, sortedMessages, formatDate) && (
            <FlexRow justify="center" scroll={false}>
              <Label text={formatDate(message.createdAt)} />
            </FlexRow>
          )}
          {isShowNew(index, sortedMessages) && (
            <Divider>
              <Label text="New" color="primary" />
            </Divider>
          )}
          <FlexColumn space={0} align={message.createdBy?.id === currentUserId ? 'flex-end' : 'flex-start'} px={2}>
            <FlexRow align="flex-end" scroll={false} ref={index + 1 === sortedMessages.length ? ref : undefined}>
              {message.createdBy?.id === currentUserId ? (
                <Label text={formatTime(message.createdAt)} variant="caption" />
              ) : (
                <Avatar alt={message.createdBy?.name ?? ''} url={message.createdBy?.icon} />
              )}
              <FlexColumn>
                {(() => {
                  const filteredReadUsers = (message.readUsers ?? []).filter(
                    (user) => user.id !== currentUserId && user.id !== message.createdBy?.id
                  )
                  if (filteredReadUsers.length === 0) return null
                  return (
                    <FlexRow
                      align="center"
                      space={0}
                      justify={message.createdBy?.id === currentUserId ? 'flex-end' : 'flex-start'}
                    >
                      <Label text={`既読 ${filteredReadUsers.length}`} variant="caption" color="secondary" />
                      <AvatarGroup total={filteredReadUsers.length}>
                        {filteredReadUsers.map((user) => (
                          <Avatar key={user.id} alt={user.name} url={user.icon} size="sm" />
                        ))}
                      </AvatarGroup>
                    </FlexRow>
                  )
                })()}
                <Card color={message.createdBy?.id === currentUserId ? 'primary' : 'info'} size="full">
                  <FlexColumn>
                    {message.destinations?.map((user) => (
                      <FlexRow align="flex-end">
                        <Label text="TO" variant="caption" />
                        <Avatar alt={user.name} url={user.icon} size="sm" />
                        <Label text={user.name} variant="caption" />
                      </FlexRow>
                    ))}
                    <TextBox>{message.description}</TextBox>
                    {message.attachments?.some((a) => isImageAttachment(a)) && (
                      <FlexColumn space={1}>
                        <FlexRow wrap space={1}>
                          {message.attachments
                            .filter((a) => isImageAttachment(a))
                            .map((attachment) => (
                              <FlexColumn key={attachment.id} space={0}>
                                <Box sx={{ cursor: 'pointer' }} onClick={() => handleOpenPreview(message, attachment)}>
                                  <Image url={attachment.url} alt={attachment.name} width={120} borderRadius={4} />
                                </Box>
                                <FlexRow align="center" space={0}>
                                  <AttachmentImageIcon size={16} />
                                  <Label text={attachment.name} variant="caption" />
                                </FlexRow>
                              </FlexColumn>
                            ))}
                        </FlexRow>
                      </FlexColumn>
                    )}
                    {message.attachments?.length > 0 && (
                      <FlexColumn space={0}>
                        {message.attachments.map((attachment) => (
                          <FlexRow key={attachment.id} align="center" space={0}>
                            <AttachmentIcon size={16} />
                            <Link
                              href={attachment.url}
                              color={message.createdBy?.id === currentUserId ? 'white.main' : 'black.main'}
                            >
                              <Label text={attachment.name} variant="caption" />
                            </Link>
                          </FlexRow>
                        ))}
                      </FlexColumn>
                    )}
                  </FlexColumn>
                </Card>
              </FlexColumn>
              {message.createdBy?.id === currentUserId ? (
                <Avatar alt={message.createdBy.name} url={message.createdBy.icon} />
              ) : (
                <Label text={formatTime(message.createdAt)} variant="caption" />
              )}
            </FlexRow>
            <Label text={message.createdBy?.name ?? ''} variant="caption" />
          </FlexColumn>
        </FlexColumn>
      ))}
      {previewIndex >= 0 && previewImages[previewIndex] && (
        <ImageDialog open width={800} onClose={handleClosePreview}>
          <FlexRow align="center" justify="center">
            <Box sx={{ position: 'absolute', left: 8, zIndex: 1 }}>
              <IconButton label="前の画像" onClick={handlePrevImage} disabled={previewIndex === 0}>
                <ArrowBackIcon />
              </IconButton>
            </Box>
            <ImageViewer src={previewImages[previewIndex].url} alt={previewImages[previewIndex].name} />
            <Box sx={{ position: 'absolute', right: 8, zIndex: 1 }}>
              <IconButton
                label="次の画像"
                onClick={handleNextImage}
                disabled={previewIndex === previewImages.length - 1}
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          </FlexRow>
        </ImageDialog>
      )}
    </FlexColumn>
  )
}
